import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'

import { ApiClientError } from '../../api/client'
import { getOrgBranding } from '../../api/org'
import { listOrganizations } from '../../api/organizations'

import {
  type BindingInput,
  type CompileResult,
  type LibraryDefinition,
  type LibraryDefinitionDetail,
  promptLibraryApi,
} from '../../api/prompt-library'
import { useDialog } from '../../components/dialogs/useDialog'
import PromptMarkdown from '../../components/prompts/PromptMarkdown'
import PromptMarkdownEditor from '../../components/prompts/PromptMarkdownEditor'
import { useAuth } from '../../hooks/useAuth'
import { resolveOrgSlug, SUPERADMIN_DEFAULT_ORG_SLUG } from '../../utils/org-slug'

type OrgScope = { kind: 'org' } | { kind: 'all' } | { kind: 'product' }

const STATUS_OPTIONS = ['DRAFT', 'ACTIVE', 'DEPRECATED', 'ARCHIVED'] as const
const CATEGORY_OPTIONS = ['engineering', 'product', 'knowledge', 'management', 'custom'] as const
const BINDING_MODES = ['append', 'prepend', 'replace', 'disable'] as const

function scopeLabel(scope: OrgScope, orgName: string | null): string {
  if (scope.kind === 'all') return 'All organizations'
  if (scope.kind === 'product') return 'Context Harbor built-in'
  return orgName ?? 'This organization'
}

function formatBindingMode(mode: string): string {
  return mode.length === 0 ? mode : mode[0]!.toUpperCase() + mode.slice(1)
}

export default function LegacyOrgPromptsPage() {
  const { user } = useAuth()
  const dialog = useDialog()
  const isSuperadmin = user?.role === 'superadmin'
  // Cross-org ("All organizations") is HQ-only, mirroring the backend's
  // isGlobalAdminHq: a superadmin switched into any other org stays scoped to it.
  const isHqSuperadmin = isSuperadmin && resolveOrgSlug() === SUPERADMIN_DEFAULT_ORG_SLUG
  const [orgName, setOrgName] = useState<string | null>(null)
  // Org id → slug for the cross-org ("All organizations") list tag. The
  // prompt-library list rows carry only the numeric orgId, so resolve the
  // human-readable slug (e.g. 1 → pradeepcgtech) via the org roster.
  const [orgSlugs, setOrgSlugs] = useState<Record<number, string>>({})
  const [scope, setScope] = useState<OrgScope>({ kind: 'org' })
  const effectiveScope: OrgScope = !isSuperadmin
    ? { kind: 'org' }
    : scope.kind === 'all' && !isHqSuperadmin
      ? { kind: 'org' }
      : scope
  useEffect(() => {
    let cancelled = false
    getOrgBranding()
      .then((branding) => {
        if (!cancelled) setOrgName(branding.name)
      })
      .catch(() => {
        // Best-effort label; falls back to "This organization" when it fails.
      })
    // Superadmins can see the cross-org list; preload the id→slug map so
    // its `· org …` tag shows the real slug (pradeepcgtech) instead of `1`.
    if (isSuperadmin) {
      listOrganizations()
        .then((orgs) => {
          if (cancelled) return
          setOrgSlugs(Object.fromEntries(orgs.filter((o) => o.isActive).map((o) => [o.id, o.slug])))
        })
        .catch(() => {
          // Best-effort map; the tag falls back to the numeric org id.
        })
    }
    return () => {
      cancelled = true
    }
  }, [isSuperadmin])

  const [defs, setDefs] = useState<LibraryDefinition[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [detail, setDetail] = useState<LibraryDefinitionDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [preview, setPreview] = useState<CompileResult | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [newName, setNewName] = useState('')
  const [newDescription, setNewDescription] = useState('')
  const [newCategory, setNewCategory] = useState('')
  const [newBody, setNewBody] = useState('')
  const [busy, setBusy] = useState(false)
  const [editingMeta, setEditingMeta] = useState(false)
  const [metaName, setMetaName] = useState('')
  const [metaDescription, setMetaDescription] = useState('')
  const [metaCategory, setMetaCategory] = useState('')
  const [metaTags, setMetaTags] = useState('')
  const [showRevision, setShowRevision] = useState(false)
  const [revisionBody, setRevisionBody] = useState('')
  const [revisionSummary, setRevisionSummary] = useState('')
  const [showBindingEditor, setShowBindingEditor] = useState<string | null>(null)
  const [bindingMode, setBindingMode] = useState<BindingInput['inheritanceMode']>('append')
  const [bindingText, setBindingText] = useState('')
  const [showOrgBinding, setShowOrgBinding] = useState(false)
  const [orgBindingMode, setOrgBindingMode] = useState<BindingInput['inheritanceMode']>('append')
  const [orgBindingText, setOrgBindingText] = useState('')

  // This scope shows CH-wide/shared definitions only (no org's private rows).
  // Accepts an explicit scope override for callers that just changed `scope`
  // via setState — React hasn't re-rendered yet, so the `effectiveScope`
  // closure below would still reflect the pre-change value.
  const load = useCallback(
    async (scopeOverride?: OrgScope) => {
      const activeScope = scopeOverride ?? effectiveScope
      setLoading(true)
      try {
        const query: { q?: string; status?: string; category?: string; scope?: string } = {}
        if (filter.trim().length > 0) query.q = filter.trim()
        if (statusFilter !== '') query.status = statusFilter
        if (categoryFilter !== '') query.category = categoryFilter
        if (activeScope.kind === 'all') query.scope = 'all'
        const rows = await promptLibraryApi.list(query)
        const visible =
          activeScope.kind === 'product'
            ? rows.filter((d) => d.isProduct)
            : rows.filter((d) => !d.isProduct)
        setDefs(visible)
      } catch {
        toast.error('Failed to load prompt library')
      } finally {
        setLoading(false)
      }
    },
    [filter, statusFilter, categoryFilter, effectiveScope]
  )

  useEffect(() => {
    void load()
  }, [load])

  const openDetail = async (id: string) => {
    setSelectedId(id)
    setDetail(null)
    setPreview(null)
    setShowRevision(false)
    setShowBindingEditor(null)
    setEditingMeta(false)
    setDetailLoading(true)
    try {
      const next = await promptLibraryApi.get(id)
      setDetail(next)
      setMetaName(next.name)
      setMetaDescription(next.description ?? '')
      setMetaCategory(next.category ?? '')
      setMetaTags(next.tags.join(', '))
      setRevisionBody(next.activeVersion?.instructions ?? '')
      setRevisionSummary('')
    } catch {
      toast.error('Failed to load prompt definition')
    } finally {
      setDetailLoading(false)
    }
  }

  const runAndReload = async (
    label: string,
    fn: () => Promise<string | null | unknown>,
    selectId?: string | null
  ) => {
    setBusy(true)
    try {
      const produced = await fn()
      toast.success(label)
      await load()
      // `fn` may return the id of a newly created row; prefer it over the
      // possibly-stale `selectedId` captured before the mutation.
      const targetId =
        typeof produced === 'string' ? produced : selectId !== undefined ? selectId : selectedId
      if (targetId !== null) {
        if (typeof produced === 'string') setSelectedId(produced)
        try {
          openDetailSilently(targetId)
        } catch {
          setDetail(null)
        }
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : `${label} failed`)
    } finally {
      setBusy(false)
    }
  }

  const openDetailSilently = async (id: string) => {
    const next = await promptLibraryApi.get(id)
    setDetail(next)
    setMetaName(next.name)
    setMetaDescription(next.description ?? '')
    setMetaCategory(next.category ?? '')
    setMetaTags(next.tags.join(', '))
    setRevisionBody(next.activeVersion?.instructions ?? '')
  }

  const canEditSelected =
    detail !== null && (isSuperadmin || (detail.orgId !== null && !detail.isProduct))

  const createDefinition = () => {
    if (newName.trim().length === 0) {
      toast.error('Name is required')
      return
    }
    const effectiveScopeKind = effectiveScope.kind
    if ((effectiveScopeKind === 'all' || effectiveScopeKind === 'product') && !isSuperadmin) {
      toast.error('Only a superadmin can create shared definitions — import one instead')
      return
    }
    // Context Harbor built-ins are HQ-only, matching the backend: a
    // superadmin switched into any other org can browse built-ins but must
    // switch back to pradeepcgtech to create one.
    if (effectiveScopeKind === 'product' && !isHqSuperadmin) {
      toast.error(
        'Creating a Context Harbor built-in requires the HQ organization — import an existing one instead'
      )
      return
    }
    const name = newName.trim()
    const description = newDescription.trim()
    const category = newCategory.trim()
    const body = newBody
    void runAndReload('Definition created', async () => {
      const created = await promptLibraryApi.create({
        name,
        ...(description.length > 0 ? { description } : {}),
        ...(category.length > 0 ? { category } : {}),
        // CH-wide/shared definitions are product rows; org scope creates org rows.
        ...(effectiveScopeKind === 'org' ? {} : { isProduct: true }),
      })
      if (body.trim().length > 0) {
        const version = await promptLibraryApi.createVersion(created.id, {
          instructions: body,
        })
        await promptLibraryApi.publishVersion(created.id, version.version)
      }
      // Returned so runAndReload selects and opens the new row.
      return created.id
    }).finally(() => {
      setShowCreate(false)
      setNewName('')
      setNewDescription('')
      setNewCategory('')
      setNewBody('')
    })
  }

  const saveMeta = () => {
    if (selectedId === null || detail === null) return
    if (metaName.trim().length === 0) {
      toast.error('Name is required')
      return
    }
    const tags = metaTags
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t.length > 0)
    void runAndReload('Definition updated', () =>
      promptLibraryApi.updateDefinition(selectedId, {
        name: metaName.trim(),
        description: metaDescription.trim().length > 0 ? metaDescription.trim() : null,
        category: metaCategory.trim().length > 0 ? metaCategory.trim() : null,
        tags,
      })
    ).finally(() => setEditingMeta(false))
  }

  const saveRevision = () => {
    if (selectedId === null) return
    if (revisionBody.trim().length === 0) {
      toast.error('Revision body is empty')
      return
    }
    const summary = revisionSummary.trim()
    void runAndReload('New revision published', async () => {
      const version = await promptLibraryApi.createVersion(selectedId, {
        instructions: revisionBody,
        ...(summary.length > 0 ? { changeSummary: summary } : {}),
      })
      await promptLibraryApi.publishVersion(selectedId, version.version)
    }).finally(() => {
      setShowRevision(false)
      setRevisionSummary('')
    })
  }

  const publishDraft = (version: number) => {
    if (selectedId === null) return
    void runAndReload(
      'Version published',
      () => promptLibraryApi.publishVersion(selectedId, version),
      selectedId
    )
  }

  const activateDefinition = () => {
    if (selectedId === null || detail === null) return
    // Definition-level activation requires ≥1 published version (FR-1).
    // Publishing any version auto-activates; this covers rows whose
    // versions were published before that behavior existed.
    const active = detail.versions.find((v) => v.status === 'ACTIVE')
    if (!active) {
      toast.error('Publish a version first — activation needs one published version')
      return
    }
    void runAndReload(
      'Definition activated',
      () => promptLibraryApi.updateDefinition(selectedId, { status: 'ACTIVE' }),
      selectedId
    )
  }

  const deprecateVersion = (version: number) => {
    if (selectedId === null) return
    void runAndReload('Version deprecated', () =>
      promptLibraryApi.deprecateVersion(selectedId, version)
    )
  }

  const importIntoOrg = () => {
    if (selectedId === null || detail === null) return
    // Import under the shared title itself — the backend dedupes: same
    // body reports ALREADY_IMPORTED, different body adds a revision.
    // No "(org copy)" suffix is ever created.
    const name = detail.name
    const action = async () => {
      const forked = await promptLibraryApi.fork(selectedId, { name })
      if (forked.imported) {
        await promptLibraryApi.publishVersion(forked.definition.id, forked.version)
      }
      setScope({ kind: 'org' })
      await openDetailSilently(forked.definition.id)
      setSelectedId(forked.definition.id)
      return forked.imported
    }
    setBusy(true)
    action()
      .then((imported) => {
        toast.success(
          imported
            ? 'Imported into your organization'
            : 'New revision added to the existing org prompt'
        )
        // Explicit override: `setScope` above hasn't re-rendered yet, so the
        // list must not filter by the stale (e.g. "built-in") scope.
        void load({ kind: 'org' })
      })
      .catch((e: unknown) => {
        // Same title + same body: nothing to do, just open the org row.
        if (e instanceof ApiClientError && e.apiError.error === 'ALREADY_IMPORTED') {
          const existingId = (e.apiError as { detail?: { definitionId?: string } }).detail
            ?.definitionId
          toast.success('Already in your organization with the same content')
          setScope({ kind: 'org' })
          if (existingId) {
            setSelectedId(existingId)
            void openDetailSilently(existingId)
          } else {
            void load({ kind: 'org' })
          }
          return
        }
        toast.error(e instanceof Error ? e.message : 'Import failed')
      })
      .finally(() => {
        setBusy(false)
      })
  }

  const archiveDefinition = async () => {
    if (selectedId === null || detail === null) return
    const confirmed = await dialog.confirm({
      title: `Archive "${detail.name}"?`,
      description: 'It stays restorable.',
      confirmLabel: 'Archive',
      tone: 'danger',
    })
    if (!confirmed) return
    void runAndReload('Definition archived', () => promptLibraryApi.archive(selectedId))
  }

  const restoreDefinition = () => {
    if (selectedId === null) return
    void runAndReload('Definition restored', () => promptLibraryApi.restore(selectedId))
  }

  const previewCompile = async () => {
    if (selectedId === null) return
    try {
      // Org-level preview: resolve without project scope (org chain only).
      setPreview(await promptLibraryApi.resolve(selectedId, {}))
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Preview failed')
    }
  }

  const toggleBinding = (bindingId: string, revision: number, enabled: boolean) => {
    if (selectedId === null) return
    void runAndReload(enabled ? 'Binding re-enabled' : 'Binding disabled', () =>
      promptLibraryApi.patchBinding(selectedId, bindingId, { enabled, revision })
    )
  }

  const saveBindingEdit = async (bindingId: string, revision: number) => {
    if (selectedId === null) return
    if (bindingMode === 'disable') {
      const confirmed = await dialog.confirm({
        title: 'Disable this binding?',
        description: 'Descendant scopes re-resolve without it.',
        confirmLabel: 'Disable',
        tone: 'danger',
      })
      if (!confirmed) return
    }
    void runAndReload('Binding updated', () =>
      promptLibraryApi.patchBinding(selectedId, bindingId, {
        inheritanceMode: bindingMode,
        ...(bindingText.trim().length > 0 ? { instructionsOverride: bindingText } : {}),
        revision,
      })
    ).finally(() => setShowBindingEditor(null))
  }

  const addOrgBinding = async () => {
    if (selectedId === null || detail === null) return
    const orgId = detail.orgId
    if (orgId === null) {
      toast.error('Shared definitions cannot take org bindings here — import first')
      return
    }
    if (orgBindingMode === 'disable') {
      const confirmed = await dialog.confirm({
        title: `Disable "${detail.name}" for this organization?`,
        description: 'Descendant scopes re-resolve without it.',
        confirmLabel: 'Disable',
        tone: 'danger',
      })
      if (!confirmed) return
    }
    void runAndReload('Organization binding saved', () =>
      promptLibraryApi.upsertBinding(selectedId, {
        scopeType: 'ORGANIZATION',
        scopeId: orgId,
        inheritanceMode: orgBindingMode,
        ...(orgBindingText.trim().length > 0 ? { instructionsOverride: orgBindingText } : {}),
      })
    ).finally(() => {
      setShowOrgBinding(false)
      setOrgBindingText('')
    })
  }

  const startBindingEdit = (bindingId: string) => {
    const binding = detail?.bindings.find((b) => b.id === bindingId)
    setShowBindingEditor(bindingId)
    setBindingMode(
      (binding?.inheritanceMode as BindingInput['inheritanceMode'] | undefined) ?? 'append'
    )
    setBindingText('')
  }

  return (
    <div className="mx-auto max-w-6xl px-6 py-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-harbor-text">Prompt library</h1>
          <p className="mt-1 text-sm text-harbor-text-secondary">
            Shared definitions, versions, and scope bindings for{' '}
            {scopeLabel(effectiveScope, orgName).toLowerCase()}.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isSuperadmin && (
            <select
              aria-label="Library scope"
              value={effectiveScope.kind}
              onChange={(e) => setScope({ kind: e.target.value as OrgScope['kind'] })}
              className="rounded-lg border border-harbor-border bg-harbor-surface-raised px-2.5 py-1.5 text-sm text-harbor-text"
            >
              <option value="org">{orgName ?? 'This organization'}</option>
              {isHqSuperadmin && <option value="all">All organizations</option>}
              <option value="product">Context Harbor built-in</option>
            </select>
          )}
          <button
            type="button"
            onClick={() => setShowCreate((v) => !v)}
            className="rounded-lg border border-emerald-500/40 px-3 py-1.5 text-sm font-medium text-emerald-400 hover:border-emerald-500/70"
          >
            New definition
          </button>
        </div>
      </div>

      <div className="mt-6 flex flex-col gap-2 sm:flex-row">
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void load()
          }}
          placeholder="Filter by name or slug…"
          className="w-full rounded-lg border border-harbor-border bg-harbor-surface px-4 py-2.5 text-sm text-harbor-text placeholder-harbor-text-muted"
        />
        <div className="flex gap-2">
          <select
            aria-label="Status filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-lg border border-harbor-border bg-harbor-surface px-3 py-2.5 text-sm text-harbor-text"
          >
            <option value="">All statuses</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <select
            aria-label="Category filter"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="rounded-lg border border-harbor-border bg-harbor-surface px-3 py-2.5 text-sm text-harbor-text"
          >
            <option value="">All categories</option>
            {CATEGORY_OPTIONS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => void load()}
            className="shrink-0 rounded-lg border border-harbor-border px-4 py-2.5 text-sm font-medium text-harbor-text hover:border-emerald-500/50"
          >
            Search
          </button>
        </div>
      </div>

      {showCreate && (
        <div className="mt-4 rounded-xl border border-harbor-border bg-harbor-surface p-5">
          <h2 className="text-sm font-semibold text-harbor-text">
            New {effectiveScope.kind === 'org' ? (orgName ?? 'organization') : 'shared'} definition
          </h2>
          <div className="mt-3 space-y-3">
            <input
              aria-label="Title"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Title…"
              className="w-full rounded-lg border border-harbor-border bg-harbor-surface-raised px-3 py-2 text-sm text-harbor-text"
            />
            <div className="flex gap-2">
              <input
                aria-label="Description"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="Description (optional)…"
                className="w-full rounded-lg border border-harbor-border bg-harbor-surface-raised px-3 py-2 text-sm text-harbor-text"
              />
              <input
                aria-label="Category"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                placeholder="Category (optional)…"
                className="w-48 rounded-lg border border-harbor-border bg-harbor-surface-raised px-3 py-2 text-sm text-harbor-text"
              />
            </div>
            <PromptMarkdownEditor value={newBody} onChange={setNewBody} />
            <p className="text-xs text-harbor-text-muted">
              Saving with a body creates the first version and publishes it. Saving with an empty
              body creates a draft shell to revise later.
            </p>
            <div>
              <button
                type="button"
                onClick={createDefinition}
                disabled={busy}
                className="rounded-lg border border-emerald-500/40 px-3 py-2 text-sm font-medium text-emerald-400 disabled:opacity-40"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        <div className="overflow-hidden rounded-xl border border-harbor-border bg-harbor-surface">
          {loading ? (
            <p className="px-6 py-10 text-center text-sm text-harbor-text-secondary">Loading…</p>
          ) : defs.length === 0 ? (
            <p className="px-6 py-10 text-center text-sm text-harbor-text-secondary">
              No prompt definitions.
            </p>
          ) : (
            <ul className="divide-y divide-harbor-border">
              {defs.map((def) => (
                <li key={def.id}>
                  <button
                    type="button"
                    onClick={() => void openDetail(def.id)}
                    className={`block w-full px-5 py-4 text-left transition-colors hover:bg-harbor-surface-raised/60 ${
                      selectedId === def.id ? 'bg-harbor-surface-raised/60' : ''
                    }`}
                  >
                    <span className="text-sm font-semibold text-harbor-text">{def.name}</span>
                    <span className="mt-1 block font-mono text-xs text-harbor-text-muted">
                      {def.slug} · {def.status}
                      {def.isProduct && ' · built-in'}
                      {def.category && ` · ${def.category}`}
                      {def.orgId !== null &&
                        effectiveScope.kind === 'all' &&
                        ` · org ${orgSlugs[def.orgId] ?? def.orgId}`}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="overflow-hidden rounded-xl border border-harbor-border bg-harbor-surface">
          {detailLoading ? (
            <p className="px-6 py-10 text-center text-sm text-harbor-text-secondary">Loading…</p>
          ) : detail === null ? (
            <p className="px-6 py-10 text-center text-sm text-harbor-text-secondary">
              Select a definition to inspect versions, bindings, and preview.
            </p>
          ) : (
            <div className="px-6 py-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-harbor-text">{detail.name}</h2>
                  <p className="mt-1 font-mono text-xs text-harbor-text-muted">
                    {detail.slug} · {detail.status} · usage {detail.usageCount}
                  </p>
                  {detail.description && (
                    <p className="mt-2 text-sm text-harbor-text-secondary">{detail.description}</p>
                  )}
                  {detail.tags.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {detail.tags.map((tag) => (
                        <span
                          key={tag}
                          className="rounded-md border border-harbor-border bg-harbor-surface-raised px-2 py-0.5 font-mono text-xs text-harbor-text-secondary"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {canEditSelected && detail.status !== 'ARCHIVED' && (
                    <button
                      type="button"
                      onClick={() => setEditingMeta((v) => !v)}
                      disabled={busy}
                      className="rounded-lg border border-harbor-border px-3 py-1.5 text-xs font-medium text-harbor-text hover:border-emerald-500/50 disabled:opacity-40"
                    >
                      Edit
                    </button>
                  )}
                  {canEditSelected && detail.status === 'DRAFT' && (
                    <button
                      type="button"
                      onClick={activateDefinition}
                      disabled={busy}
                      title="Activate the definition (needs one published version)"
                      className="rounded-lg border border-emerald-500/40 px-3 py-1.5 text-xs font-medium text-emerald-400 hover:border-emerald-500/70 disabled:opacity-40"
                    >
                      Activate
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => void importIntoOrg()}
                    disabled={busy}
                    title="Import under the same title — same content reports already-imported, different content adds a revision"
                    className="rounded-lg border border-sky-500/40 px-3 py-1.5 text-xs font-medium text-sky-300 hover:border-sky-500/70 disabled:opacity-40"
                  >
                    Import into my org
                  </button>
                  {canEditSelected && detail.status === 'ARCHIVED' ? (
                    <button
                      type="button"
                      onClick={restoreDefinition}
                      disabled={busy}
                      className="rounded-lg border border-emerald-500/40 px-3 py-1.5 text-xs font-medium text-emerald-400 disabled:opacity-40"
                    >
                      Restore
                    </button>
                  ) : (
                    canEditSelected && (
                      <button
                        type="button"
                        onClick={archiveDefinition}
                        disabled={busy}
                        className="rounded-lg border border-red-500/40 px-3 py-1.5 text-xs font-medium text-red-300 disabled:opacity-40"
                      >
                        Archive
                      </button>
                    )
                  )}
                </div>
              </div>

              {editingMeta && canEditSelected && (
                <div className="mt-4 space-y-2 rounded-lg border border-harbor-border bg-harbor-surface-raised/40 p-4">
                  <input
                    aria-label="Title"
                    value={metaName}
                    onChange={(e) => setMetaName(e.target.value)}
                    className="w-full rounded-lg border border-harbor-border bg-harbor-surface px-3 py-2 text-sm text-harbor-text"
                  />
                  <input
                    aria-label="Description"
                    value={metaDescription}
                    onChange={(e) => setMetaDescription(e.target.value)}
                    placeholder="Description…"
                    className="w-full rounded-lg border border-harbor-border bg-harbor-surface px-3 py-2 text-sm text-harbor-text"
                  />
                  <div className="flex gap-2">
                    <input
                      aria-label="Category"
                      value={metaCategory}
                      onChange={(e) => setMetaCategory(e.target.value)}
                      placeholder="Category…"
                      className="w-full rounded-lg border border-harbor-border bg-harbor-surface px-3 py-2 text-sm text-harbor-text"
                    />
                    <input
                      aria-label="Tags"
                      value={metaTags}
                      onChange={(e) => setMetaTags(e.target.value)}
                      placeholder="Tags, comma-separated…"
                      className="w-full rounded-lg border border-harbor-border bg-harbor-surface px-3 py-2 text-sm text-harbor-text"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={saveMeta}
                      disabled={busy}
                      className="rounded-lg border border-emerald-500/40 px-3 py-1.5 text-xs font-medium text-emerald-400 disabled:opacity-40"
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingMeta(false)}
                      className="rounded-lg border border-harbor-border px-3 py-1.5 text-xs font-medium text-harbor-text"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              <h3 className="mt-6 text-sm font-semibold uppercase tracking-wider text-harbor-text-muted">
                Versions
              </h3>
              <ul className="mt-2 space-y-2">
                {detail.versions.map((v) => (
                  <li
                    key={v.version}
                    className="flex items-center justify-between gap-2 rounded-lg border border-harbor-border bg-harbor-surface-raised/40 px-3 py-2"
                  >
                    <span className="font-mono text-xs text-harbor-text">
                      v{v.version} · {v.status}
                      {v.changeSummary && ` · ${v.changeSummary}`}
                    </span>
                    <span className="flex gap-2">
                      {v.status === 'DRAFT' && canEditSelected && (
                        <button
                          type="button"
                          onClick={() => publishDraft(v.version)}
                          disabled={busy}
                          className="rounded-md border border-emerald-500/40 px-2 py-1 text-xs font-medium text-emerald-400 disabled:opacity-40"
                        >
                          Publish
                        </button>
                      )}
                      {v.status === 'ACTIVE' && canEditSelected && (
                        <button
                          type="button"
                          onClick={() => deprecateVersion(v.version)}
                          disabled={busy}
                          className="rounded-md border border-amber-500/40 px-2 py-1 text-xs font-medium text-amber-300 disabled:opacity-40"
                        >
                          Deprecate
                        </button>
                      )}
                    </span>
                  </li>
                ))}
              </ul>

              {detail.activeVersion && (
                <details className="mt-4">
                  <summary className="cursor-pointer text-xs text-harbor-text-muted">
                    Active version content
                  </summary>
                  <div className="mt-2">
                    <PromptMarkdown
                      markdown={detail.activeVersion.instructions}
                      maxHeightClass="max-h-48"
                    />
                  </div>
                </details>
              )}

              {canEditSelected && detail.status !== 'ARCHIVED' && (
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={() => setShowRevision((v) => !v)}
                    disabled={busy}
                    className="rounded-lg border border-harbor-border px-3 py-1.5 text-xs font-medium text-harbor-text hover:border-emerald-500/50 disabled:opacity-40"
                  >
                    {showRevision ? 'Close revision editor' : 'New revision'}
                  </button>
                </div>
              )}

              {showRevision && canEditSelected && (
                <div className="mt-3 space-y-3 rounded-lg border border-harbor-border bg-harbor-surface-raised/40 p-4">
                  <PromptMarkdownEditor
                    value={revisionBody}
                    onChange={setRevisionBody}
                    label="Revision body (Markdown)"
                  />
                  <input
                    aria-label="Change summary"
                    value={revisionSummary}
                    onChange={(e) => setRevisionSummary(e.target.value)}
                    placeholder="Change summary (optional)…"
                    className="w-full rounded-lg border border-harbor-border bg-harbor-surface px-3 py-2 text-sm text-harbor-text"
                  />
                  <p className="text-xs text-harbor-text-muted">
                    Saving creates a new immutable version and publishes it. Editing never rewrites
                    history.
                  </p>
                  <button
                    type="button"
                    onClick={saveRevision}
                    disabled={busy}
                    className="rounded-lg border border-emerald-500/40 px-3 py-1.5 text-xs font-medium text-emerald-400 disabled:opacity-40"
                  >
                    Save and publish revision
                  </button>
                </div>
              )}

              <div className="mt-6 flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-harbor-text-muted">
                  Bindings ({detail.bindings.length})
                </h3>
                {canEditSelected && detail.orgId !== null && detail.status !== 'ARCHIVED' && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowOrgBinding((v) => !v)
                      setOrgBindingMode('append')
                    }}
                    disabled={busy}
                    className="rounded-lg border border-harbor-border px-3 py-1.5 text-xs font-medium text-harbor-text hover:border-emerald-500/50 disabled:opacity-40"
                  >
                    Add organization binding
                  </button>
                )}
              </div>
              {showOrgBinding && canEditSelected && detail.orgId !== null && (
                <div className="mt-3 space-y-2 rounded-lg border border-harbor-border bg-harbor-surface-raised/40 p-4">
                  <div className="flex gap-2">
                    <select
                      aria-label="Organization binding mode"
                      value={orgBindingMode}
                      onChange={(e) =>
                        setOrgBindingMode(e.target.value as BindingInput['inheritanceMode'])
                      }
                      className="rounded-lg border border-harbor-border bg-harbor-surface px-3 py-2 text-sm text-harbor-text"
                    >
                      {BINDING_MODES.map((m) => (
                        <option key={m} value={m}>
                          {formatBindingMode(m)}
                        </option>
                      ))}
                    </select>
                    <input
                      aria-label="Organization binding text"
                      value={orgBindingText}
                      onChange={(e) => setOrgBindingText(e.target.value)}
                      placeholder="Override text (empty = policy-only append; replace asks to confirm)…"
                      className="w-full rounded-lg border border-harbor-border bg-harbor-surface px-3 py-2 text-sm text-harbor-text"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={addOrgBinding}
                    disabled={busy}
                    className="rounded-lg border border-emerald-500/40 px-3 py-1.5 text-xs font-medium text-emerald-400 disabled:opacity-40"
                  >
                    Save binding
                  </button>
                </div>
              )}
              {detail.bindings.length === 0 ? (
                <p className="mt-2 text-xs text-harbor-text-muted">No scope bindings.</p>
              ) : (
                <ul className="mt-2 space-y-2">
                  {detail.bindings.map((b) => (
                    <li
                      key={b.id}
                      className="rounded-lg border border-harbor-border bg-harbor-surface-raised/40 px-3 py-2"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-mono text-xs text-harbor-text">
                          {b.scopeType}:{b.scopeId} [{b.inheritanceMode}]{' '}
                          {b.enabled ? '' : '(disabled)'}
                        </span>
                        {canEditSelected && (
                          <span className="flex shrink-0 gap-2">
                            {showBindingEditor !== b.id && (
                              <button
                                type="button"
                                onClick={() => startBindingEdit(b.id)}
                                disabled={busy}
                                className="rounded-md border border-harbor-border px-2 py-1 text-xs font-medium text-harbor-text disabled:opacity-40"
                              >
                                Edit
                              </button>
                            )}
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => toggleBinding(b.id, b.revision, !b.enabled)}
                              className={`rounded-md border px-2 py-1 text-xs font-medium disabled:opacity-40 ${
                                b.enabled
                                  ? 'border-red-500/40 text-red-300'
                                  : 'border-emerald-500/40 text-emerald-400'
                              }`}
                            >
                              {b.enabled ? 'Disable' : 'Re-enable'}
                            </button>
                          </span>
                        )}
                      </div>
                      {showBindingEditor === b.id && canEditSelected && (
                        <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                          <select
                            aria-label="Binding mode"
                            value={bindingMode}
                            onChange={(e) =>
                              setBindingMode(e.target.value as BindingInput['inheritanceMode'])
                            }
                            className="rounded-lg border border-harbor-border bg-harbor-surface px-2 py-1.5 text-xs text-harbor-text"
                          >
                            {BINDING_MODES.map((m) => (
                              <option key={m} value={m}>
                                {formatBindingMode(m)}
                              </option>
                            ))}
                          </select>
                          <input
                            aria-label="Binding override text"
                            value={bindingText}
                            onChange={(e) => setBindingText(e.target.value)}
                            placeholder="Override text (empty keeps existing)…"
                            className="w-full rounded-lg border border-harbor-border bg-harbor-surface px-2 py-1.5 text-xs text-harbor-text"
                          />
                          <button
                            type="button"
                            onClick={() => saveBindingEdit(b.id, b.revision)}
                            disabled={busy}
                            className="shrink-0 rounded-md border border-emerald-500/40 px-2 py-1 text-xs font-medium text-emerald-400 disabled:opacity-40"
                          >
                            Save
                          </button>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}

              <div className="mt-6 flex gap-2">
                <button
                  type="button"
                  onClick={() => void previewCompile()}
                  className="rounded-lg border border-harbor-border px-3 py-1.5 text-xs font-medium text-harbor-text hover:border-emerald-500/50"
                >
                  Preview effective prompt
                </button>
              </div>
              {preview !== null && (
                <div className="mt-3 space-y-2">
                  <div className="flex flex-wrap gap-1.5">
                    {preview.layers.map((layer, i) => (
                      <span
                        key={`${layer.scopeType}:${layer.scopeId ?? 'x'}:${i}`}
                        className="rounded-md border border-harbor-border bg-harbor-surface-raised px-2 py-0.5 font-mono text-xs text-harbor-text-secondary"
                      >
                        {layer.scopeType}:{layer.scopeName ?? layer.scopeId ?? 'runtime'} [
                        {layer.mode}]
                      </span>
                    ))}
                  </div>
                  {preview.effectiveInstructions ? (
                    <PromptMarkdown
                      markdown={preview.effectiveInstructions}
                      maxHeightClass="max-h-48"
                    />
                  ) : (
                    <p className="rounded-lg border border-harbor-border bg-harbor-surface-raised/60 px-3 py-2 font-mono text-xs text-harbor-text-secondary">
                      (no body — disabled)
                    </p>
                  )}
                  <p className="font-mono text-xs text-harbor-text-muted">
                    tokens≈{preview.estimatedTokens.total} ·{' '}
                    {preview.compilationFingerprint.slice(0, 16)}…
                  </p>
                  {preview.warnings.length > 0 && (
                    <ul className="list-disc pl-5 text-xs text-amber-300">
                      {preview.warnings.map((w) => (
                        <li key={w}>{w}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
